import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import StatusBadge from "../components/StatusBadge";
import { Loader2, Pencil, Plus, Archive, RotateCcw, Trash2, Globe, PlugZap, Copy, Ban } from "lucide-react";
import { toast } from "sonner";
import { ConnectionBadge } from "../components/connectionStatus";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const STATUSES = ["Active", "Setup Required", "Coming Soon", "Planned"];

const slug = (name) => name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

const emptyForm = (order = 0) => ({
  name: "",
  description: "",
  logo: "",
  domain: "",
  category: "",
  source_key: "",
  public_url: "",
  os_url: "",
  os_note: "",
  display_order: order,
  status: "Setup Required",
  show_on_public: true,
});

export default function Administration() {
  const [active, setActive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [connBiz, setConnBiz] = useState(null);
  const [connData, setConnData] = useState(null);
  const [revealedKey, setRevealedKey] = useState("");
  const [rotating, setRotating] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const openConnection = async (b) => {
    setConnBiz(b); setConnData(null); setRevealedKey(""); setConfirmAction(null);
    try {
      const { data } = await api.get(`/ecosystem/businesses/${b.id}/connection`);
      setConnData(data);
    } catch (e) { toast.error("Could not load connection info"); }
  };

  const rotateKey = async () => {
    setConfirmAction(null);
    setRotating(true);
    try {
      const { data } = await api.post(`/ecosystem/businesses/${connBiz.id}/connection/rotate-key`);
      setRevealedKey(data.signing_secret);
      const { data: fresh } = await api.get(`/ecosystem/businesses/${connBiz.id}/connection`);
      setConnData(fresh);
      toast.success("Signing secret issued — copy it now");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setRotating(false); }
  };

  const revokeKey = async () => {
    setConfirmAction(null);
    setRotating(true);
    try {
      await api.post(`/ecosystem/businesses/${connBiz.id}/connection/revoke`);
      setRevealedKey("");
      const { data: fresh } = await api.get(`/ecosystem/businesses/${connBiz.id}/connection`);
      setConnData(fresh);
      toast.success("Connection revoked");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setRotating(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/businesses"), api.get("/businesses/archived")])
      .then(([a, ar]) => {
        setActive(a.data);
        setArchived(ar.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => {
    setFormMode("create");
    setEditingId(null);
    setForm(emptyForm(active.length));
    setFormOpen(true);
  };

  const openEdit = (b) => {
    setFormMode("edit");
    setEditingId(b.id);
    setForm({
      name: b.name,
      description: b.description || "",
      logo: b.logo || "",
      domain: b.domain || "",
      category: b.category || "",
      source_key: b.source_key || "",
      public_url: b.public_url || "",
      os_url: b.os_url || "",
      os_note: b.os_note || "",
      display_order: b.display_order ?? 0,
      status: b.status,
      show_on_public: b.show_on_public !== false,
    });
    setFormOpen(true);
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Business name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, display_order: Number(form.display_order) };
      if (!payload.source_key) delete payload.source_key;
      if (formMode === "create") {
        await api.post("/businesses", payload);
        toast.success("Business added");
      } else {
        await api.put(`/businesses/${editingId}`, payload);
        toast.success("Business updated");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const archiveBiz = async (b) => {
    try {
      await api.post(`/businesses/${b.id}/archive`);
      toast.success(`${b.name} archived`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Archive failed");
    }
  };

  const restoreBiz = async (b) => {
    try {
      await api.post(`/businesses/${b.id}/restore`);
      toast.success(`${b.name} restored`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/businesses/${deleteTarget.id}`);
      toast.success("Business permanently deleted");
      setDeleteTarget(null);
      setDeleteText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#581C87] font-semibold">
            Administration
          </p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-2">
            Manage Business Registry
          </h1>
          <p className="text-gray-500 mt-2">
            Add, edit, archive, and restore businesses in your ecosystem.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#581C87] hover:bg-[#6B21A8] text-white shrink-0"
          data-testid="add-business-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Business
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#581C87]" />
        </div>
      ) : (
        <>
          {/* Active registry */}
          <div className="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-widest text-gray-500 font-semibold">
              <div className="col-span-1">Order</div>
              <div className="col-span-4">Business</div>
              <div className="col-span-3">Domain · Destinations</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {active.length === 0 && (
              <div className="px-6 py-10 text-center text-gray-400 text-sm" data-testid="active-empty">
                No active businesses. Click "Add Business" to create one.
              </div>
            )}
            {active.map((b) => (
              <div
                key={b.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 border-b border-gray-100 items-center"
                data-testid={`admin-row-${slug(b.name)}`}
              >
                <div className="md:col-span-1 font-mono text-sm text-gray-400">#{b.display_order}</div>
                <div className="md:col-span-4 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#581C87] to-[#7C3AED] flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {b.logo}
                  </div>
                  <span className="font-medium text-sm text-gray-900">{b.name}</span>
                </div>
                <div className="md:col-span-3 text-sm text-gray-500 font-mono truncate">
                  {b.domain || "—"}
                  <div className="flex gap-2 mt-0.5 text-[10px] font-sans" data-testid={`admin-destinations-${slug(b.name)}`}>
                    <span className={b.public_url ? "text-emerald-600" : "text-gray-400"}>Website {b.public_url ? "✓" : "—"}</span>
                    <span className={b.os_url ? "text-emerald-600" : "text-gray-400"}>OS {b.os_url ? "✓" : "—"}</span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <StatusBadge status={b.status} />
                </div>
                <div className="md:col-span-2 md:text-right flex md:justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openConnection(b)}
                    data-testid={`connection-button-${slug(b.name)}`}
                    className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]"
                    title="Integration Vault"
                    aria-label={`Integration Vault for ${b.name}`}
                  >
                    <PlugZap className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(b)}
                    data-testid={`edit-button-${slug(b.name)}`}
                    aria-label={`Edit ${b.name}`}
                    className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => archiveBiz(b)}
                    data-testid={`archive-button-${slug(b.name)}`}
                    aria-label={`Archive ${b.name}`}
                    className="border-gray-200 hover:border-amber-500 hover:text-amber-600"
                  >
                    <Archive className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Archived section */}
          <div className="mt-12" data-testid="archived-section">
            <h2 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <Archive className="h-5 w-5 text-gray-400" />
              Archived Businesses
              <span className="text-sm font-normal text-gray-400">({archived.length})</span>
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Archived businesses are hidden from active views but preserved in the database.
            </p>

            {archived.length === 0 ? (
              <div className="mt-4 bg-white border border-dashed border-gray-200 rounded-xl px-6 py-10 text-center text-gray-400 text-sm" data-testid="archived-empty">
                No archived businesses.
              </div>
            ) : (
              <div className="mt-4 bg-white border border-gray-200 rounded-xl overflow-hidden">
                {archived.map((b) => (
                  <div
                    key={b.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 border-b border-gray-100 items-center"
                    data-testid={`archived-row-${slug(b.name)}`}
                  >
                    <div className="md:col-span-6 flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold shrink-0">
                        {b.logo}
                      </div>
                      <div>
                        <span className="font-medium text-sm text-gray-700">{b.name}</span>
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 font-mono mt-0.5">
                          <Globe className="h-3 w-3" />
                          {b.domain || "no domain"}
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <StatusBadge status={b.status} />
                    </div>
                    <div className="md:col-span-4 md:text-right flex md:justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => restoreBiz(b)}
                        data-testid={`restore-button-${slug(b.name)}`}
                        className="border-gray-200 hover:border-emerald-500 hover:text-emerald-600"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        Restore
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDeleteTarget(b);
                          setDeleteText("");
                        }}
                        data-testid={`delete-button-${slug(b.name)}`}
                        className="border-gray-200 hover:border-red-500 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add / Edit form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="business-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {formMode === "create" ? "Add Business" : "Edit Business"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Business Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => upd("name", e.target.value)}
                className="focus-visible:ring-[#581C87]"
                data-testid="form-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Short Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => upd("description", e.target.value)}
                className="focus-visible:ring-[#581C87]"
                data-testid="form-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Logo (initials)</Label>
                <Input
                  value={form.logo}
                  onChange={(e) => upd("logo", e.target.value)}
                  maxLength={3}
                  placeholder="KU"
                  className="focus-visible:ring-[#581C87]"
                  data-testid="form-logo-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.display_order}
                  onChange={(e) => upd("display_order", e.target.value)}
                  className="focus-visible:ring-[#581C87]"
                  data-testid="form-order-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Domain</Label>
                <Input
                  value={form.domain}
                  onChange={(e) => upd("domain", e.target.value)}
                  placeholder="example.com"
                  className="focus-visible:ring-[#581C87]"
                  data-testid="form-domain-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => upd("category", e.target.value)}
                  placeholder="Education"
                  className="focus-visible:ring-[#581C87]"
                  data-testid="form-category-input"
                />
              </div>
            </div>
            {formMode === "edit" && (
              <div className="space-y-2">
                <Label>Source Key (event identifier)</Label>
                <Input
                  value={form.source_key}
                  onChange={(e) => upd("source_key", e.target.value)}
                  placeholder="auto-generated from name"
                  className="focus-visible:ring-[#581C87] font-mono text-sm"
                  data-testid="form-source-key-input"
                />
                <p className="text-xs text-gray-400">Used by this entity's OS to report events. Changing it disconnects a connected sender until its ECOSYSTEM_SOURCE_KEY is updated.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Public Website URL</Label>
              <Input
                value={form.public_url}
                onChange={(e) => upd("public_url", e.target.value)}
                placeholder="https://www.example.com"
                className="focus-visible:ring-[#581C87]"
                data-testid="form-public-url-input"
              />
              <p className="text-xs text-gray-500">Customer-facing site. Powers the “Visit Website” button.</p>
            </div>
            <div className="space-y-2">
              <Label>Operating System URL (administrator entry point)</Label>
              <Input
                value={form.os_url}
                onChange={(e) => upd("os_url", e.target.value)}
                placeholder="https://app.example.com/admin"
                className="focus-visible:ring-[#581C87]"
                data-testid="form-os-url-input"
              />
              <p className="text-xs text-gray-500">
                The authenticated admin portal — not the public homepage. Leave empty to show “Operating System Not Configured”.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Operating System Note</Label>
              <Input
                value={form.os_note}
                onChange={(e) => upd("os_note", e.target.value)}
                placeholder="e.g. Hosted inside KCIS Technologies"
                className="focus-visible:ring-[#581C87]"
                data-testid="form-os-note-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                <SelectTrigger data-testid="form-status-select" className="focus:ring-[#581C87]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`status-option-${slug(s)}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
              <div>
                <Label className="cursor-pointer">Show on public website</Label>
                <p className="text-xs text-gray-500 mt-0.5">Include this business on the future public site.</p>
              </div>
              <Switch
                checked={form.show_on_public}
                onCheckedChange={(v) => upd("show_on_public", v)}
                data-testid="form-show-public-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFormOpen(false)} data-testid="form-cancel-button">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#581C87] hover:bg-[#6B21A8] text-white"
              data-testid="form-save-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : formMode === "create" ? "Create Business" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Permanently Delete Business</DialogTitle>
            <DialogDescription>
              This will permanently delete <span className="font-semibold text-gray-900">{deleteTarget?.name}</span>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
            <Input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="DELETE"
              className="focus-visible:ring-red-500"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel-button">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteText !== "DELETE" || deleting}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="delete-confirm-button"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Permanently Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connection management */}
      <Dialog open={!!connBiz} onOpenChange={(o) => { if (!o) { setConnBiz(null); setRevealedKey(""); } }}>
        <DialogContent className="max-w-lg" data-testid="connection-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><PlugZap className="h-4 w-4 text-[#581C87]" /> Integration Vault — {connBiz?.name}</DialogTitle>
            <DialogDescription>
              Server-to-server signed events (HMAC-SHA256). The secret belongs only in the sending OS's backend environment — never in a browser. See ECOSYSTEM_EVENTS.md.
            </DialogDescription>
          </DialogHeader>
          {!connData ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#581C87]" /></div>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="space-y-1.5">
                <Label className="text-xs">Webhook endpoint (POST)</Label>
                <div className="flex gap-2">
                  <Input readOnly value={`${BACKEND}${connData.webhook_path}`} className="font-mono text-xs" data-testid="conn-webhook-url" />
                  <Button variant="outline" size="sm" onClick={() => copy(`${BACKEND}${connData.webhook_path}`)} className="border-gray-200"><Copy className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">source_key</Label>
                  <Input readOnly value={connData.source_key || ""} className="font-mono text-xs" data-testid="conn-source-key" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <div className="h-9 flex items-center">
                    <ConnectionBadge status={connData.connection_status} testId="conn-status" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center" data-testid="conn-health">
                <div className="rounded-lg bg-gray-50 py-2"><p className="font-bold">{connData.health.accepted_24h}</p><p className="text-[10px] text-gray-500">accepted 24h</p></div>
                <div className="rounded-lg bg-gray-50 py-2"><p className="font-bold">{connData.health.duplicates_24h}</p><p className="text-[10px] text-gray-500">duplicates 24h</p></div>
                <div className="rounded-lg bg-gray-50 py-2"><p className={`font-bold ${connData.health.rejected_24h ? "text-red-600" : ""}`}>{connData.health.rejected_24h}</p><p className="text-[10px] text-gray-500">rejected 24h</p></div>
              </div>
              {connData.health.last_rejection_reason && (
                <p className="text-xs text-red-600" data-testid="conn-last-rejection">Last rejection: {connData.health.last_rejection_reason}</p>
              )}
              {!connData.server_signing_configured && (
                <p className="text-xs text-red-600">Server signing key (ECOSYSTEM_SECRET_KEY) is not configured — events cannot be accepted.</p>
              )}

              <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs">Signing secret</Label>
                  <div className="flex gap-2">
                    {connData.has_credential && (
                      <Button onClick={() => setConfirmAction("revoke")} disabled={rotating} size="sm" variant="outline" className="border-gray-200 hover:border-red-500 hover:text-red-600" data-testid="revoke-key-button">
                        <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
                      </Button>
                    )}
                    <Button onClick={() => (connData.has_credential ? setConfirmAction("rotate") : rotateKey())} disabled={rotating} size="sm" className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="rotate-key-button">
                      {rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (connData.has_credential ? "Rotate Secret" : "Issue Secret")}
                    </Button>
                  </div>
                </div>
                {confirmAction && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-2.5 text-xs text-red-700 flex flex-wrap items-center justify-between gap-2" data-testid="confirm-credential-action">
                    <span>{confirmAction === "rotate" ? "Rotating stops the current secret immediately — the sending OS must be updated." : "Revoking disconnects this business; its events will be refused."}</span>
                    <span className="flex gap-2">
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => setConfirmAction(null)} data-testid="confirm-cancel-button">Cancel</Button>
                      <Button size="sm" className="h-7 bg-red-600 hover:bg-red-700 text-white" onClick={confirmAction === "rotate" ? rotateKey : revokeKey} data-testid="confirm-proceed-button">
                        Confirm {confirmAction === "rotate" ? "rotation" : "revoke"}
                      </Button>
                    </span>
                  </div>
                )}
                {revealedKey ? (
                  <div className="flex gap-2">
                    <Input readOnly value={revealedKey} className="font-mono text-xs" data-testid="revealed-key" />
                    <Button variant="outline" size="sm" onClick={() => copy(revealedKey)} className="border-gray-200"><Copy className="h-3.5 w-3.5" /></Button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    {connData.has_credential ? `Active secret (${connData.credential_prefix || ""}••••), encrypted at rest. Rotate to issue a new one; the old one stops working immediately.` : "No credential. Issue a secret to authorize this business to send events. It is shown only once."}
                  </p>
                )}
                {revealedKey && <p className="text-xs text-amber-600">Copy this secret now — it cannot be retrieved again.</p>}
              </div>

              <p className="text-xs text-gray-400">Headers: X-Ecosystem-Source, X-Ecosystem-Timestamp, X-Ecosystem-Signature. Event types: {connData.event_types.join(", ")}.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConnBiz(null); setRevealedKey(""); }} data-testid="connection-close-button">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
